import { Link } from "react-router-dom";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip } from "recharts";
import { CheckCircle2 } from "lucide-react";
import { useApi, fmtCost } from "@/lib/api";
import { PageHeader, Kpi, Card, StatusBadge } from "@/components/common";

const DEMO_PATH = [
  ["Deux agents spécialisés et un agent d'accueil", "/agents"], ["Plusieurs outils affectés à un agent", "/agents"],
  ["Création et affectation d'un skill", "/skills"], ["Import d'un document et suivi de l'indexation", "/documents"],
  ["Réponse documentée avec source", "/studio"], ["Routage automatique et changement d'agent", "/studio"],
  ["Test texte puis vocal dans le navigateur", "/studio"], ["Évaluation d'une réponse et historique", "/historique"],
  ["Modèles différents par agent", "/fournisseurs"], ["Simulation d'appel avec transfert et fin d'appel", "/telephonie"],
];

export default function Dashboard() {
  const { data } = useApi("/dashboard");
  const { data: integ } = useApi("/system/integrations");
  return (
    <div>
      <PageHeader title="Tableau de bord" subtitle="Vue d'ensemble de l'exploitation des agents IA sur les 7 derniers jours." />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi label="Agents actifs" value={data ? `${data.agents.active}/${data.agents.total}` : "…"} testid="kpi-agents" />
        <Kpi label="Conversations (7 j)" value={data?.conversations_7d ?? "…"} sub={`${data?.responses_7d ?? 0} réponses`} testid="kpi-conversations" />
        <Kpi label="Latence moyenne" value={data ? `${data.avg_latency_ms} ms` : "…"} sub={`Coût estimé : ${fmtCost(data?.cost_7d_eur)}`} testid="kpi-latency" />
        <Kpi label="Satisfaction" value={data ? `${Math.round(data.evaluations.useful_rate * 100)} %` : "…"} sub={`${data?.evaluations.total ?? 0} évaluations · note ${data?.evaluations.avg_rating ?? 0}/5`} testid="kpi-quality" />
      </div>
      <div className="grid lg:grid-cols-3 gap-4 mt-4">
        <Card className="lg:col-span-2">
          <h3 className="font-semibold mb-4">Réponses par jour</h3>
          <div className="h-56">
            <ResponsiveContainer>
              <BarChart data={data?.daily || []}>
                <XAxis dataKey="day" fontSize={11} stroke="hsl(var(--muted-foreground))" />
                <YAxis fontSize={11} stroke="hsl(var(--muted-foreground))" allowDecimals={false} />
                <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))" }} />
                <Bar dataKey="responses" name="Réponses" fill="hsl(var(--primary))" radius={4} maxBarSize={48} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
        <Card>
          <h3 className="font-semibold mb-3">État des intégrations</h3>
          <div className="space-y-2" data-testid="integrations-status">
            {(integ || []).map((i) => (
              <div key={i.name} className="flex items-start justify-between gap-2 text-sm">
                <div className="min-w-0"><div className="truncate">{i.name}</div><div className="text-[11px] text-muted-foreground truncate">{i.detail}</div></div>
                <StatusBadge status={i.status} />
              </div>
            ))}
          </div>
        </Card>
      </div>
      <div className="grid lg:grid-cols-2 gap-4 mt-4">
        <Card>
          <h3 className="font-semibold mb-3">Parcours de démonstration</h3>
          <ol className="space-y-1.5">
            {DEMO_PATH.map(([l, to], i) => (
              <li key={l}><Link to={to} className="flex items-center gap-3 text-sm px-2 py-1.5 rounded hover:bg-muted transition-colors" data-testid={`demo-step-${i + 1}`}>
                <CheckCircle2 className="h-4 w-4 text-primary shrink-0" /><span className="text-muted-foreground mono text-xs">{String(i + 1).padStart(2, "0")}</span>{l}</Link></li>
            ))}
          </ol>
        </Card>
        <Card>
          <h3 className="font-semibold mb-3">Documents & erreurs récentes</h3>
          <div className="flex gap-2 flex-wrap mb-4">
            {Object.entries(data?.documents || {}).map(([s, n]) => <StatusBadge key={s} status={s} label={`${n} · ${s}`} />)}
          </div>
          {(data?.recent_errors || []).length === 0 ? <p className="text-sm text-muted-foreground">Aucune erreur de fournisseur récente.</p> :
            data.recent_errors.map((e, i) => <div key={i} className="text-xs border-l-2 border-rose-500 pl-2 mb-2"><b>{e.provider} / {e.model}</b><div className="text-muted-foreground break-all">{e.error}</div></div>)}
        </Card>
      </div>
    </div>
  );
}
